import type { FC } from "react";
import { Avatar, Button, Card, Form, Typography } from "antd";
import { UserOutlined } from "@ant-design/icons";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import { getIndustryLabel, industryOptions } from "src/utils/industryOptions";
import type { TFunction } from "i18next";

const { Link } = Typography;

interface AccountProfileCardProps {
  t: TFunction;
  isAdmin: boolean;
  isViewer: boolean;
  profileImage?: string;
  firstName: string;
  lastName: string;
  email: string;
  websiteUrl: string;
  industry: string;
  currency?: string;
  supportedCurrencies: string[];
  company: string;
  contactNumber?: string;
  firstNameError: string;
  lastNameError: string;
  isUpdatingProfile: boolean;
  isDirty: boolean;
  onFirstNameChange: (_value: string) => void;
  onLastNameChange: (_value: string) => void;
  onWebsiteUrlChange: (_value: string) => void;
  onIndustryChange: (_value: string) => void;
  onCurrencyChange: (_value: string) => void;
  onPhotoUpload: (_file: File) => void;
  onRemovePhoto: () => void;
  onProfileUpdate: () => void;
  onAdminOnlyWarning: (_messageKey: string) => void;
}

const AccountProfileCard: FC<AccountProfileCardProps> = ({
  t,
  isAdmin,
  isViewer,
  profileImage,
  firstName,
  lastName,
  email,
  websiteUrl,
  industry,
  currency,
  supportedCurrencies,
  company,
  contactNumber,
  firstNameError,
  lastNameError,
  isUpdatingProfile,
  isDirty,
  onFirstNameChange,
  onLastNameChange,
  onWebsiteUrlChange,
  onIndustryChange,
  onCurrencyChange,
  onPhotoUpload,
  onRemovePhoto,
  onProfileUpdate,
  onAdminOnlyWarning,
}) => (
  <Card title={t("accountSettings.accountInfoTitle")} className="account-card">
    <div className="account-avatar-section">
      <Avatar
        size={120}
        className={`account-avatar ${!profileImage ? "no-photo" : "has-photo"}`}
        src={profileImage}
        icon={!profileImage && <UserOutlined />}
      />
      <div className="account-photo-actions">
        <input
          type="file"
          accept="image/*"
          id="profile-upload"
          className="profile-upload-input"
          onClick={e => {
            if (!isAdmin) {
              e.preventDefault();
              onAdminOnlyWarning("accountSettings.warnings.adminOnlyPhoto");
            }
          }}
          onChange={e => {
            if (!isAdmin) {
              return;
            }
            const file = e.target.files?.[0];
            if (file) {
              onPhotoUpload(file);
            }
          }}
        />

        <label htmlFor="profile-upload" className="photo-label">
          <Link className="photo-btns">{t("accountSettings.uploadPhoto")}</Link>
        </label>
        {profileImage && (
          <>
            <span className="divider">|</span>
            <label className="photo-label">
              <Link
                onClick={() => {
                  if (!isAdmin) {
                    onAdminOnlyWarning("accountSettings.warnings.adminOnlyPhoto");
                    return;
                  }
                  onRemovePhoto();
                }}
                className="photo-btns"
              >
                {t("accountSettings.removePhoto")}
              </Link>
            </label>
          </>
        )}
      </div>
    </div>

    <Form layout="vertical" className="account-form">
      <Form.Item label={t("accountSettings.labels.firstName")}>
        <StyledTextInput
          value={firstName}
          onChange={e => onFirstNameChange(e.target.value)}
          className="account-field--editable"
        />
        {firstNameError && <div className="error-message">{firstNameError}</div>}
      </Form.Item>

      <Form.Item label={t("accountSettings.labels.lastName")}>
        <StyledTextInput
          value={lastName}
          onChange={e => onLastNameChange(e.target.value)}
          className="account-field--editable"
        />
        {lastNameError && <div className="error-message">{lastNameError}</div>}
      </Form.Item>

      <Form.Item label={t("accountSettings.labels.email")}>
        <StyledTextInput value={email} disabled />
      </Form.Item>

      <Form.Item label={t("accountSettings.labels.websiteUrl")}>
        <StyledTextInput
          value={websiteUrl}
          onChange={e => onWebsiteUrlChange(e.target.value)}
          disabled={!isAdmin}
          className={isAdmin ? "account-field--editable" : undefined}
          onClick={() => {
            if (isViewer) {
              onAdminOnlyWarning("accountSettings.warnings.adminOnlyWebsite");
            }
          }}
        />
      </Form.Item>

      <Form.Item label={t("accountSettings.labels.company")}>
        <StyledTextInput value={company} disabled />
      </Form.Item>

      <Form.Item label={t("accountSettings.labels.industry")}>
        <StyledDropdown
          value={industry || undefined}
          className={`account-industry-dropdown${isAdmin ? " account-field--editable" : ""}`}
          placeholder={t("userIntro.selectIndustry")}
          onChange={value => onIndustryChange(value as string)}
          disabled={!isAdmin}
          onClick={() => {
            if (isViewer) {
              onAdminOnlyWarning("accountSettings.warnings.adminOnlyIndustry");
            }
          }}
          options={industryOptions.map(option => ({
            label: getIndustryLabel(option, t),
            value: option,
          }))}
          suffixIcon={<span className="styled-dropdown-caret" aria-hidden="true" />}
        />
      </Form.Item>

      <Form.Item label={t("accountSettings.labels.currency")}>
        <StyledDropdown
          value={currency}
          placeholder={t("accountSettings.labels.currency")}
          className={`account-industry-dropdown${isAdmin ? " account-field--editable" : ""}`}
          onChange={value => onCurrencyChange(value as string)}
          disabled={!isAdmin}
          onClick={() => {
            if (isViewer) {
              onAdminOnlyWarning("accountSettings.warnings.adminOnlyCurrency");
            }
          }}
          options={supportedCurrencies.map(code => ({ label: code, value: code }))}
          suffixIcon={<span className="styled-dropdown-caret" aria-hidden="true" />}
        />
      </Form.Item>

      <Form.Item label={t("accountSettings.labels.contactNumber")}>
        <StyledTextInput value={contactNumber} disabled />
      </Form.Item>

      <Button
        type="primary"
        className="button button--primary setting-btn"
        onClick={onProfileUpdate}
        loading={isUpdatingProfile}
        disabled={!isDirty}
      >
        {isUpdatingProfile
          ? t("accountSettings.updating")
          : t("accountSettings.update")}
      </Button>
    </Form>
  </Card>
);

export default AccountProfileCard;
